import { Box, H6, Stack } from "@adfenix/alexandria";
import { When } from "components/When/When";
import { useState } from "react";
import { Collapse } from "react-collapse";
import { ImageTemplateContent } from "./ImageTemplateContent";
import { SelectableTemplate } from "./SelectableTemplate";
import {
  getAvailableTemplatesKey,
  AvailableTemplate
} from "./AvailableTemplate";
import { TemplateContent } from "./TemplateContent";
import { ToggleButton } from "./ToggleButton";
import { ProgramService } from "@adfenix/types";
import { TemplateUiModel } from "routes/back-office/program-builder/details/campaign/meta/template/TemplateUiModel";
import { PreviewTemplateModal } from "routes/back-office/hq-settings/templates/campaign-templates/modal/preview/TemplatePreviewModal";
import { useFormContext } from "react-hook-form";
import { useDisabledFields } from "../../../useDisabledFields";
import { useMetaTemplateContext } from "../../util/CampaignTemplateContext";

interface Props {
  title: string;
  allTemplates: TemplateUiModel[];
  type: "image" | "ad";
}

export function TemplateGroup({ title, allTemplates, type }: Props) {
  const disabledFields = useDisabledFields();
  const [addOther, setAddOther] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);

  const { campaignKey } = useMetaTemplateContext();
  const { watch, setValue } = useFormContext<ProgramService.ProgramModel>();

  const availableTemplateIds =
    watch(`${campaignKey}.${getAvailableTemplatesKey(type)}`) ?? [];

  const availableTemplates = availableTemplateIds
    .map(x => allTemplates.find(y => y.id === x))
    .filter(x => typeof x !== "undefined") as TemplateUiModel[];

  const subjectType = watch("subjectType") ?? "Property";

  const selectableTemplates: TemplateUiModel[] = allTemplates.filter(x =>
    availableTemplates.every(y => x.id !== y.id)
  );

  return (
    <Box id={`template-group-${type}`} style={{ position: "relative" }}>
      <Stack spacing="s">
        <H6>{title}</H6>
        <PreviewTemplateModal
          templateId={preview}
          parentSelector={`#template-group-${type}`}
          channel="facebook"
          onClose={() => setPreview(null)}
          type={type}
          subjectType={subjectType}
        />

        <Stack spacing="s">
          {availableTemplates.map(x => (
            <AvailableTemplate
              template={x}
              key={x.id}
              onPreview={() => setPreview(x.id)}
              selectableTemplates={selectableTemplates}
            >
              {x.type === "ad" ? (
                <TemplateContent template={x} />
              ) : (
                <ImageTemplateContent template={x} />
              )}
            </AvailableTemplate>
          ))}
        </Stack>
      </Stack>
      <When truthy={selectableTemplates.length > 0}>
        <ToggleButton
          isToggled={addOther}
          setToggle={setAddOther}
          toggleMessage={`Add another ${
            type === "ad" ? "text" : "image"
          } template`}
          toggledMessage="Cancel"
        />
      </When>

      <Collapse isOpened={addOther}>
        {selectableTemplates.map(x => {
          return (
            <SelectableTemplate
              disabled={disabledFields(
                type === "ad"
                  ? "bundleGroups.bundles.campaignTemplates.availableAdTemplates"
                  : "bundleGroups.bundles.campaignTemplates.availableImageTemplates"
              )}
              key={x.id}
              template={x}
              mode="add"
              onSelect={template => {
                setAddOther(false);
                setValue(
                  `${campaignKey}.${getAvailableTemplatesKey(x.type)}`,
                  [template.id].concat(availableTemplates.map(x => x.id)),
                  { shouldDirty: true }
                );
              }}
            />
          );
        })}
      </Collapse>
    </Box>
  );
}
